import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import './Message.scss';
import { convertFromRaw, EditorState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import DOMPurify from 'dompurify';

import linkifyIt from 'linkify-it';
import tlds from 'tlds';
import AttachmentPreview from '../AttachmentPreview';

const linkify = linkifyIt();
linkify.tlds(tlds);

const MessageContent = (props) => {
  const [showMore, setShowMore] = useState(false);

  const [editorObject, setEditorObject] = useState(null);
  const [content, setContent] = useState(null);
  const { onAttachmentClick } = props;

  useEffect(() => {
    if (props.content !== content) {
      setContent(props.content);
    }
    if (props.editor_object !== editorObject) {
      setEditorObject(props.editor_object);
    }
  }, [props.content, props.editorObject]);

  const createStrategy = (tag) => (contentBlock, callback, contentState) => {
    return contentBlock.findEntityRanges((char) => {
      const entityKey = char.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === tag
      );
    }, callback);
  };

  const decorators = [
    {
      strategy: createStrategy('ATTACHMENT'),
      component: (props) => {
        const attachment = props.contentState
          .getEntity(props.entityKey)
          .getData();
        return (
          <AttachmentPreview attachment={attachment} onClick={onAttachmentClick} width={200}/>
        )
      }
    },
    {
      strategy: createStrategy('VIDEO'),
      component: (props) => {
        const entityData = props.contentState
          .getEntity(props.entityKey)
          .getData();
        return (
          <video
            src={entityData.src}
            controls={entityData.controls}
            style={{ maxWidth: '100%' }}
          />
        );
      },
    },
    {
      strategy: createStrategy('AUDIO'),
      component: (props) => {
        const entityData = props.contentState
          .getEntity(props.entityKey)
          .getData();
        return (
          <audio
            src={entityData.src}
            controls={entityData.controls}
            style={{ maxWidth: '100%' }}
          />
        );
      },
    },
    {
      // This should be last in the array so we don't process videos/images as links
      strategy: (contentBlock, callback) => {
        const links = linkify.match(contentBlock.get('text'));
        if (typeof links !== 'undefined' && links !== null) {
          for (let i = 0; i < links.length; i += 1) {
            callback(links[i].index, links[i].lastIndex);
          }
        }
      },
      component: (props) => {
        const { url, text, schema } = linkify.match(props.decoratedText)[0];
        return (
          <a target={schema !== 'mailto:' ? '_blank' : ''} href={url}>
            {text}
          </a>
        );
      },
    },
  ];

  const parseStringForLinks = (lineString) => {
    const links = linkify.match(lineString);
    if (!links) {
      return lineString;
    }
    return links.reduce(
      ({ elements, lastIndex }, linkMatch, index) => {
        let newElements = [];
        // If we have text between this link and the previous one we want to keep it
        if (linkMatch.index > lastIndex) {
          newElements.push(lineString.substring(lastIndex, linkMatch.index));
        }
        // Push the link element
        newElements.push(
          <a
            target={linkMatch.schema !== 'mailto:' ? '_blank' : ''}
            href={linkMatch.url}
          >
            {linkMatch.text}
          </a>
        );
        // If this is the last link and there is text after it we want to keep it
        if (
          index === links.length - 1 &&
          linkMatch.lastIndex < lineString.length
        ) {
          newElements.push(
            lineString.substring(linkMatch.lastIndex, lineString.length)
          );
        }
        return Object.assign({
          elements: elements.concat(newElements),
          lastIndex: linkMatch.lastIndex,
        });
      },
      { elements: [], lastIndex: 0 }
    ).elements;
  };

  const getContent = () => {
    const { id, html, fullContent } = props;
    if (editorObject) {
      return (
        <Editor
          editorState={EditorState.createWithContent(
            convertFromRaw(editorObject)
          )}
          readOnly={true}
          toolbarHidden={true}
          customDecorators={decorators}
          toolbar={{ link: { showOpenOptionOnHover: false } }}
        />
      );
    }
    if (html) {
      const sanitized = DOMPurify(window).sanitize(html);
      return (
        <div
          className="MessageContent-html"
          dangerouslySetInnerHTML={{ __html: sanitized }}
        ></div>
      );
    }
    if (content) {
      const sanitizedContent = showMore
        ? fullContent.split('\n')
        : content.split('\n');
      return (
        <span>
          {sanitizedContent.map((line, index) => {
            return (
              <span key={`${index}-line-${id}`}>
                {parseStringForLinks(line, id)}
                <br />
              </span>
            );
          })}
        </span>
      );
    }
    return null;
  };

  const { fullContent } = props;

  if (!editorObject && typeof content !== 'string') {
    return null;
  }
  const hasMore = fullContent && fullContent.length !== content.length;
  return (
    <span className="MessageContent">
      {getContent()}
      {hasMore ? (
        <span
          className="MessageContent-show-more"
          onClick={() => setShowMore(!showMore)}
        >
          {showMore ? 'Show less' : 'Show more'}
        </span>
      ) : null}
    </span>
  );
};

MessageContent.propTypes = {
  fullContent: PropTypes.string,
  editor_object: PropTypes.object,
  html: PropTypes.string,
  content: PropTypes.string,
  id: PropTypes.number,
};

export default MessageContent;
